import { createSignal } from 'solid-js';

const STORAGE_KEY = 'div-theme';

type Theme = 'light' | 'dark';

const systemTheme = (): Theme => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

// The inline script in index.html already stamped the class before first paint;
// this reads back the same source of truth so the signal starts in sync.
const storedTheme = (): Theme | null => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'dark' || raw === 'light' ? raw : null;
};

const [theme, setThemeSignal] = createSignal<Theme>(storedTheme() ?? systemTheme());

export { theme };

export const applyTheme = (next: Theme): void => {
    setThemeSignal(next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    localStorage.setItem(STORAGE_KEY, next);
};

export const toggleTheme = (): void => applyTheme(theme() === 'dark' ? 'light' : 'dark');

export const initializeTheme = (): void => {
    document.documentElement.classList.toggle('dark', theme() === 'dark');
    // An unpinned user follows the OS. Once they pick a theme the choice is
    // stored and system changes stop overriding it.
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (storedTheme()) return;
        setThemeSignal(e.matches ? 'dark' : 'light');
        document.documentElement.classList.toggle('dark', e.matches);
    });
};

// Spacing density, read by Divider. The signal itself lives in packages/ui so a
// shared component never imports app state; this app owns the value.
export { spacing as activeSpacingId, setSpacing as setActiveSpacingId } from '~ui/config';
